// @ts-strict-ignore
import React, { useContext, useEffect, useMemo } from 'react';
import { Box, ButtonVariant, Spinner } from '@usealloy/component-library';
import ThresholdsTab from '~/dashboard/views/workflows/editor/components/NodeEditorDrawer/ThresholdsTab';
import type { Action, ThresholdResults } from '~/dashboard/views/workflows/store/engine/types';
import {
  StyledSearchBoxContainer,
  StyledSearchTriggerContainer,
  StyledSectionHeader,
  StyledWorkflowCanvasContainer,
} from './styles';
import { UserContext } from '~/dashboard/context';
import { ContainerType } from '~/dashboard/views/workflows/editor/components/NodeEditorDrawer/ThresholdsTab/types';
import { useEditorAccess } from 'flow/editor/context';
import WorkflowCanvas from 'flow/editor/components/WorkflowCanvas';
import { SearchBox, SearchTrigger } from 'flow/editor/components/Search';
import { EditorMode } from 'flow/editor/context/constants';

type TagRuleProps = {
  action: Action;
  thresholdResults: ThresholdResults;
  loading: boolean;
  canViewRules: boolean;
};

const TagRule = ({
  action,
  thresholdResults,
  loading,
  canViewRules,
}: TagRuleProps) => {
  const { featureFlags } = useContext(UserContext);

  const { setEditorMode } = useEditorAccess();
  useEffect(() => {
    setEditorMode(EditorMode.ReadOnly);
  }, [setEditorMode]);

  const tagRuleContent = (
    featureFlags?.ruleExplainabilityPhase >= 4 ? (
      <StyledWorkflowCanvasContainer
        direction="column"
        alignItems="flex-start"
      >
        <StyledSearchBoxContainer>
          <SearchBox withoutContainer />
        </StyledSearchBoxContainer>
        <WorkflowCanvas readOnly />
        <StyledSearchTriggerContainer>
          <SearchTrigger variant={ButtonVariant.tertiary} />
        </StyledSearchTriggerContainer>
      </StyledWorkflowCanvasContainer>
    ) : (
      <Box
        direction="column"
        alignItems="flex-start"
      >
        <ThresholdsTab
          action={action}
          containerType={ContainerType.MODAL}
          thresholdResults={thresholdResults || null}
        />
      </Box>
    )
  );

  const outputSectionHeader = useMemo(() => {
    if (featureFlags?.ruleExplainabilityPhase > 0) {
      return (
        <StyledSectionHeader
          size={1}
          /**
           * Specific pixel width for output values section
           * See Rule Explainability Figma design
           */
          style={{
            width: '194px',
            minWidth: '194px',
            paddingLeft: 'var(--cl-space-6)',
          }}
        >
          Output
        </StyledSectionHeader>
      );
    }

    return null;
  }, [featureFlags]);

  if (!canViewRules) {
    return null;
  }

  if (featureFlags?.ruleExplainabilityPhase >= 4) {
    return (
      <Box
        direction="column"
        alignItems="flex-start"
        gap={3}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        {loading || (!action && featureFlags.ruleExplainabilityPhase < 4) ? (
          <Box>
            <Spinner />
          </Box>
        ) : tagRuleContent}
      </Box>
    );
  }

  return (
    <Box direction="column" alignItems="flex-start" gap={3}>
      <Box justifyContent="space-between">
        <StyledSectionHeader size={1}>
          Rule
        </StyledSectionHeader>
        {outputSectionHeader}
      </Box>
      {loading || (!action && featureFlags.ruleExplainabilityPhase < 4) ? (
        <Box>
          <Spinner />
        </Box>
      ) : tagRuleContent}
    </Box>
  );
};

export default TagRule;
